/**
 * Il secondo cancello: impronta o volto quando l'app torna in primo piano.
 *
 * ## Perché in un gestionale HR
 *
 * Questa app mostra dati che il BE stesso considera particolari — l'anagrafica dei colleghi, le
 * assenze con la loro causale, i dati mascherati che compaiono solo a chi ha il claim. Sul
 * telefono, tutto questo è protetto dallo sblocco del dispositivo e basta: un telefono lasciato
 * aperto su una scrivania è un gestionale HR lasciato aperto su una scrivania.
 *
 * ## Le tre scelte che rendono la funzione usabile invece che odiosa
 *
 * 1. **È opzionale.** Si accende dalle impostazioni. Imporla farebbe disinstallare l'app, non
 *    aumentare la sicurezza.
 * 2. **Ha una soglia.** Bloccare a ogni cambio di finestra significa chiedere l'impronta per aver
 *    guardato l'ora. Sotto il minuto non si blocca: il tempo di rispondere a un messaggio.
 * 3. **Il codice del telefono vale come l'impronta** (`allowDeviceCredential`). Chi ha l'impronta
 *    bagnata o il volto coperto deve poter entrare lo stesso, altrimenti la funzione si spegne al
 *    primo giorno di pioggia.
 *
 * ## Cosa non è
 *
 * Non è cifratura: i dati in memoria restano quelli, e chi ha accesso fisico al filesystem di un
 * telefono sbloccato li raggiunge comunque. È un velo davanti allo schermo — che è esattamente il
 * rischio che copre, cioè lo sguardo di chi passa.
 */
import { nativeRuntime } from '@/shared/lib/nativePlatform';

import { leggiChiave, writeKey, removeKey } from './storage';
import { STORAGE_KEYS } from './constants';
/** Sotto questo tempo in secondo piano non si richiede nulla. */
export const THRESHOLD_MS = 60_000;

const ACTIVE_KEY = STORAGE_KEYS.appLock;

export function lockActive(): boolean {
  return leggiChiave(ACTIVE_KEY) === '1';
}

export function setLock(attivo: boolean): void {
  /* In modalità privata la preferenza non si conserva e la funzione sì: `scriviChiave` non
     solleva, quindi il ramo non ha bisogno di essere scritto qui. */
  if (attivo) writeKey(ACTIVE_KEY, '1');
  else removeKey(ACTIVE_KEY);
}

/** La regola, isolata perché è quella che si sbaglia: quanto tempo fuori fa scattare il blocco. */
export function mustLock(msInBackground: number, threshold: number = THRESHOLD_MS): boolean {
  return msInBackground >= threshold;
}

export interface BiometricsAvailability {
  available: boolean;
  /** Come si chiama qui: «impronta», «volto», o il codice del dispositivo. */
  label: string;
  /** Perché non si può usare, già pronto da mostrare. */
  message?: string;
}

/**
 * Se il dispositivo può fare da secondo cancello.
 *
 * Il tipo di biometria serve al **testo**: «Sblocca con l'impronta» e «Sblocca con Face ID» non
 * sono la stessa frase per chi legge, e una dicitura generica su iPhone sembra scritta da chi non
 * ha mai visto un iPhone.
 */
export async function availableBiometrics(): Promise<BiometricsAvailability> {
  if (nativeRuntime() === 'cordova') return cordovaAvailability();
  return {
    available: false,
    label: '',
    message: 'Il blocco biometrico funziona nell’app installata sul telefono.',
  };
}

export type UnlockOutcome =
  | { ok: true }
  | { ok: false; cause: 'annullato' | 'fallito' | 'indisponibile'; message: string };

/** Chiede lo sblocco. `annullato` non è un errore: l'utente può riprovare quando vuole. */
export async function unlock(): Promise<UnlockOutcome> {
  if (nativeRuntime() === 'cordova') return unlockCordova();
  return { ok: false, cause: 'indisponibile', message: 'Sblocco non disponibile su questa piattaforma.' };
}

/**
 * Osserva le uscite e i rientri dell'app e chiama `onBloccare` quando serve.
 *
 * Il tempo si misura sull'orologio e non su un timer: un timer, in secondo piano, viene sospeso dal
 * sistema — è il modo classico di scrivere un blocco che non scatta mai proprio quando servirebbe,
 * cioè dopo ore.
 */
export function observeReturns(onLock: () => void): () => void {
  /*
   * ⚠️ In Cordova questo usciva subito, quindi **il blocco applicazione non scattava mai**:
   * l'interruttore nelle impostazioni si attivava, `sblocca()` funzionava se lo si chiamava a
   * mano, e il rientro dopo ore non veniva riconosciuto da nessuno. Una funzione di sicurezza
   * che si accende e non fa niente e' peggio di una che non c'e': chi l'ha attivata **crede**
   * di essere protetto.
   *
   * ⚠️ Ed e' stato trovato per poco: la mia prima ricerca dei chiamanti diceva «nessuno», e
   * con due `--include` la grep aveva guardato solo i `.ts` lasciando fuori i `.tsx` — dove
   * sta l'unico chiamante, `AppLockGate`. Stavo per dichiarare morta una funzione viva e
   * lasciarla rotta.
   */
  if (nativeRuntime() !== 'cordova') return () => undefined;
  return observeCordovaReturns(onLock);
}

/* ───────────────────────────── il ramo Cordova ───────────────────────────── */

/**
 * `cordova-plugin-fingerprint-aio` non porta i tipi: si dichiara il minimo che si usa, letto
 * dalla sua firma in `plugins/cordova-plugin-fingerprint-aio/www/Fingerprint.js`.
 */
interface FingerprintCordova {
  show(
    params: {
      title: string;
      description?: string;
      disableBackup?: boolean;
      cancelButtonTitle?: string;
    },
    ok: () => void,
    ko: (e: unknown) => void,
  ): void;
  isAvailable(ok: (tipo: string) => void, ko: (e: unknown) => void): void;
}

function fingerprint(): FingerprintCordova | null {
  if (typeof window === 'undefined') return null;
  return (window as { Fingerprint?: FingerprintCordova }).Fingerprint ?? null;
}

/**
 * I codici del plugin, **letti dal suo sorgente** e non ricordati.
 *
 * ⚠️ Lares controlla `code === -128`, che in questo elenco **non esiste**: funziona lo stesso
 * perche' ha un ripiego sul testo del messaggio. Qui si usano i due codici veri — `-108` per
 * «chiuso dall'utente» e `-109` per «chiuso il codice del telefono» — e il ripiego sul testo
 * resta come rete, non come unico appiglio.
 */
const RETIRED = new Set([-108, -109]);
const NOT_SUBSCRIBED = -106;
const WITHOUT_SCREEN_LOCK = -110;
const ABSENT_HARDWARE = new Set([-101, -103, -104]);

/** `'finger'` e `'biometric'` su Android, `'face'` su iOS: sono le stringhe che il plugin manda. */
const CORDOVA_LABEL: Record<string, string> = {
  finger: 'impronta',
  face: 'volto',
  biometric: 'biometria',
};

function codeOf(e: unknown): number | null {
  const c = (e as { code?: unknown } | null)?.code;
  return typeof c === 'number' ? c : null;
}

function cordovaAvailability(): Promise<BiometricsAvailability> {
  return new Promise((resolve) => {
    const fp = fingerprint();
    if (!fp) {
      /* Il plugin manca dal confezionamento: la funzione non e' «non disponibile su questa
         piattaforma», e' assente da **questa build**. Sono due rimedi diversi. */
      resolve({
        available: false,
        label: '',
        message: 'Questa versione dell’app non ha lo sblocco biometrico.',
      });
      return;
    }
    fp.isAvailable(
      (tipo) => resolve({ available: true, label: CORDOVA_LABEL[tipo] ?? 'biometria' }),
      (e) => {
        const c = codeOf(e);
        if (c === NOT_SUBSCRIBED || c === WITHOUT_SCREEN_LOCK) {
          resolve({
            available: false,
            label: '',
            message:
              'Su questo telefono non è configurato nessuno sblocco (impronta, volto o codice): impostane uno per usare questa funzione.',
          });
          return;
        }
        if (c !== null && ABSENT_HARDWARE.has(c)) {
          resolve({
            available: false,
            label: '',
            message: 'Questo telefono non ha un sensore biometrico utilizzabile.',
          });
          return;
        }
        resolve({
          available: false,
          label: '',
          message: 'Non riesco a interrogare il sistema di sblocco di questo dispositivo.',
        });
      },
    );
  });
}

/**
 * Lo sblocco col plugin Cordova.
 *
 * ⚠️ `disableBackup: false` **serve**, ed e' la stessa scelta di Lares: chi ha il dito bagnato
 * o il volto coperto deve poter entrare col codice del telefono. Senza quel ripiego l'unica
 * uscita da un'app bloccata e' disinstallarla.
 */
function unlockCordova(): Promise<UnlockOutcome> {
  return new Promise((risolvi) => {
    const fp = fingerprint();
    if (!fp) {
      risolvi({
        ok: false,
        cause: 'indisponibile',
        message: 'Questa versione dell’app non ha lo sblocco biometrico.',
      });
      return;
    }
    fp.show(
      {
        title: 'MaeFwk10 è bloccata',
        description: 'Conferma di essere tu per continuare.',
        disableBackup: false,
        cancelButtonTitle: 'Usa il codice',
      },
      () => risolvi({ ok: true }),
      (e) => {
        const c = codeOf(e);
        const text = String((e as { message?: unknown } | null)?.message ?? e ?? '').toLowerCase();
        if ((c !== null && RETIRED.has(c)) || text.includes('cancel') || text.includes('annull')) {
          risolvi({ ok: false, cause: 'annullato', message: 'Sblocco annullato.' });
          return;
        }
        risolvi({
          ok: false,
          cause: 'fallito',
          message: 'Non è stato riconosciuto. Riprova, oppure usa il codice del dispositivo.',
        });
      },
    );
  });
}

/**
 * Il rientro in primo piano col guscio Cordova.
 *
 * ⚠️ Gli eventi sono `pause` e `resume` **su `document`**, non su `window`: e' la convenzione di
 * Cordova, e agganciarli a `window` non produce niente — senza errore.
 *
 * ⚠️ Si ascolta **anche** `visibilitychange`, e non e' ridondanza: su Android il `pause` di
 * Cordova arriva quando l'activity va in secondo piano, ma non quando lo schermo si spegne con
 * l'app davanti. Chi blocca il telefono in tasca e lo riprende dopo un'ora deve trovare il
 * blocco, ed e' proprio il caso per cui questa funzione esiste.
 *
 * Le due strade si sommano su un solo `uscitaAlle`: chi arriva secondo trova il valore gia'
 * consumato e non fa niente, quindi il blocco non scatta due volte.
 */
function observeCordovaReturns(onBloccare: () => void): () => void {
  if (typeof document === 'undefined') return () => undefined;

  let exitAt: number | null = null;

  const exited = () => { exitAt = Date.now(); };
  const returned = () => {
    if (exitAt == null) return;
    const outside = Date.now() - exitAt;
    exitAt = null;
    if (lockActive() && mustLock(outside)) onBloccare();
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') exited();
    else returned();
  };

  document.addEventListener('pause', exited, false);
  document.addEventListener('resume', returned, false);
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    document.removeEventListener('pause', exited, false);
    document.removeEventListener('resume', returned, false);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
